import { formatDemandId, isValidDemandId } from './demandId.js';

/** Months of platform invoices retained on Billing → Invoices. Older are deleted. */
export const PLATFORM_INVOICE_RETENTION_MONTHS = 12;

/** Demo receipts nested under each monthly invoice (UI stress seed). */
export const DEMO_RECEIPTS_PER_MONTH = 30;

const DEMO_DEBTORS = [
  'Acme Freight',
  'Northwind Tools',
  'Blue Harbor LLC',
  'Summit Freight Co.',
  'Cedar Ridge Supply',
  'Lakeview Motors',
  'Pioneer Dental',
  'Ironwood Logistics',
  'Brightpath Staffing',
  'Harborline Retail',
];

/**
 * Cutoff timestamp: invoices issued before this are outside the retention window.
 */
export function platformInvoiceRetentionCutoff(
  now = Date.now(),
  months = PLATFORM_INVOICE_RETENTION_MONTHS
) {
  const d = new Date(Number(now));
  d.setMonth(d.getMonth() - Number(months));
  return d.getTime();
}

/**
 * Keep invoices issued within the retention window, newest first.
 * Items without a usable issuedAt are excluded (treated as expired/unknown).
 */
export function retainPlatformInvoices(
  invoices = [],
  {
    months = PLATFORM_INVOICE_RETENTION_MONTHS,
    now = Date.now(),
  } = {}
) {
  const cutoff = platformInvoiceRetentionCutoff(now, months);
  return (Array.isArray(invoices) ? invoices : [])
    .filter((inv) => {
      const issuedAt = Date.parse(inv?.issuedAt);
      return Number.isFinite(issuedAt) && issuedAt >= cutoff;
    })
    .sort((a, b) => Date.parse(b.issuedAt) - Date.parse(a.issuedAt));
}

/**
 * Monthly invoice status follows the platform subscription receipt when present
 * (Paid / Open / Unpaid). Demand receipts stay independent.
 */
export function invoicePaymentStatus(invoice) {
  const receipts = invoiceReceipts(invoice);
  const platform = receipts.find(
    (row) => String(row?.kind || '').toLowerCase() === 'platform'
  );
  if (platform) {
    const platformStatus = String(platform.status || '').toUpperCase();
    if (platformStatus === 'PAID' || platformStatus === 'UNPAID') return platformStatus;
    return 'OPEN';
  }
  const explicit = String(invoice?.status || '').toUpperCase();
  if (explicit === 'PAID' || explicit === 'UNPAID') return explicit;
  return 'OPEN';
}

export function isInvoicePaid(invoice) {
  return invoicePaymentStatus(invoice) === 'PAID';
}

export function invoiceKind(invoice) {
  const kind = String(invoice?.kind || '').toLowerCase();
  if (kind === 'demand' || kind === 'monthly') return kind;
  if (invoice?.demandId) return 'demand';
  return 'monthly';
}

/** List title: monthly invoices show month name only (e.g. "September"). */
export function invoiceListTitle(invoice) {
  if (invoiceKind(invoice) !== 'monthly') {
    return String(invoice?.label || '').trim() || 'Invoice';
  }
  const issuedAt = Date.parse(invoice?.issuedAt);
  if (Number.isFinite(issuedAt)) {
    return new Date(issuedAt).toLocaleDateString('en-US', { month: 'long' });
  }
  return (
    String(invoice?.label || '')
      .replace(/\s+platform$/i, '')
      .replace(/\s+\d{4}\b.*$/i, '')
      .trim() || 'Invoice'
  );
}

/** Display invoice number (e.g. INV-2026-10). Falls back from issued date / id. */
export function invoiceNumber(invoice) {
  const explicit = String(invoice?.number || '').trim();
  if (explicit) return explicit;

  if (invoice?.demandId) {
    return `INV-${invoice.demandId}`;
  }

  const issuedAt = Date.parse(invoice?.issuedAt);
  if (Number.isFinite(issuedAt)) {
    const d = new Date(issuedAt);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    if (invoiceKind(invoice) === 'monthly') {
      return `INV-${yyyy}-${mm}`;
    }
  }

  const id = String(invoice?.id || '');
  const ym = id.match(/(\d{4})[_-](\d{2})/);
  if (ym) return `INV-${ym[1]}-${ym[2]}`;
  if (id) {
    return `INV-${id.replace(/^inv[_-]?/i, '').replace(/_/g, '-').toUpperCase()}`;
  }
  return '—';
}

export function invoiceReceipts(invoice) {
  return Array.isArray(invoice?.receipts) ? invoice.receipts : [];
}

/**
 * Invoice total = sum of nested payment receipt amounts when present;
 * otherwise the stored invoice.amount.
 */
export function invoiceTotalAmount(invoice) {
  const receipts = invoiceReceipts(invoice);
  if (receipts.length) {
    return receipts.reduce((sum, row) => {
      const n = Number(row?.amount);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }
  const fallback = Number(invoice?.amount);
  return Number.isFinite(fallback) ? fallback : 0;
}

/** Normalize receipt/invoice payment status for display. */
export function paymentStatusLabel(status) {
  const key = String(status || '').toUpperCase();
  if (key === 'PAID') return 'Paid';
  if (key === 'UNPAID') return 'Unpaid';
  return 'Open';
}

export function paymentStatusClass(status) {
  const key = String(status || '').toUpperCase();
  if (key === 'PAID') return 'is-paid';
  if (key === 'UNPAID') return 'is-unpaid';
  return 'is-open';
}

function buildDemoMonthReceipts(
  count,
  { year, monthIndex, monthPaid, nowMs }
) {
  const total = Math.max(0, Number(count) || 0);
  const receipts = [];
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  for (let i = 0; i < total; i += 1) {
    const day = Math.min(daysInMonth, 1 + (i % daysInMonth));
    const paidAt = new Date(year, monthIndex, day, 10 + (i % 8), (i * 7) % 60, 0, 0);
    // Keep inside retention / not in the future relative to seed clock.
    if (paidAt.getTime() > nowMs) paidAt.setTime(nowMs - i * 60 * 60 * 1000);
    const demandSeq = 1000 + monthIndex * 100 + i + 1;
    const demandId = formatDemandId(year, demandSeq);
    const debtor = DEMO_DEBTORS[i % DEMO_DEBTORS.length];
    const paid = monthPaid || i > 0;
    receipts.push({
      id: `rcpt_${year}_${String(monthIndex + 1).padStart(2, '0')}_${String(i + 1).padStart(2, '0')}`,
      kind: 'demand',
      demandId,
      label: `${demandId} · ${debtor}`,
      detail: 'Demand fee · paid upfront',
      amount: 249,
      currency: 'USD',
      status: paid ? 'PAID' : 'OPEN',
      paidAt: paid ? paidAt.toISOString() : null,
      pdfUrl: paid ? `#receipt-${year}-${monthIndex + 1}-${i + 1}` : null,
      paymentMethodLabel: paid ? 'Visa ···· 4242' : null,
    });
  }

  return receipts;
}

/**
 * Build one monthly platform invoice per month for the last `months` months.
 * Each month includes `receiptsPerMonth` nested payment receipts for UI testing.
 * Newest monthly invoice is OPEN; some prior months seed an UNPAID platform
 * receipt (declined recurring) while demand receipts stay PAID; remaining
 * prior months are PAID. Labels use the month name only.
 *
 * Unpaid platform months (relative offsets from current): 1 and 3
 * (e.g. September + July when now is October).
 */
export const DEMO_UNPAID_PLATFORM_MONTH_OFFSETS = [1, 3];

/**
 * Build one monthly platform invoice per month for the last `months` months.
 */
export function buildDemoMonthlyInvoices(
  months = PLATFORM_INVOICE_RETENTION_MONTHS,
  {
    now = Date.now(),
    receiptsPerMonth = DEMO_RECEIPTS_PER_MONTH,
    unpaidPlatformOffsets = DEMO_UNPAID_PLATFORM_MONTH_OFFSETS,
  } = {}
) {
  const total = Math.max(0, Number(months) || 0);
  const invoices = [];
  const base = new Date(Number(now));
  const nowMs = Number(now);
  const unpaidOffsets = new Set(
    (Array.isArray(unpaidPlatformOffsets) ? unpaidPlatformOffsets : []).map(Number)
  );

  for (let i = 0; i < total; i += 1) {
    const issued = new Date(base.getFullYear(), base.getMonth() - i, 1, 12, 0, 0, 0);
    const label = issued.toLocaleDateString('en-US', { month: 'long' });
    const yyyy = issued.getFullYear();
    const monthIndex = issued.getMonth();
    const mm = String(monthIndex + 1).padStart(2, '0');
    const unpaidPlatform = unpaidOffsets.has(i);
    const paid = i !== 0 && !unpaidPlatform;
    // Prior months keep demand fees PAID even when the recurring charge failed.
    const demandReceipts = buildDemoMonthReceipts(receiptsPerMonth, {
      year: yyyy,
      monthIndex,
      monthPaid: i !== 0,
      nowMs,
    });
    const platformAmount = 285;
    const platformStatus = unpaidPlatform ? 'UNPAID' : paid ? 'PAID' : 'OPEN';
    const platformReceipt = {
      id: `rcpt_platform_${yyyy}_${mm}`,
      kind: 'platform',
      label: 'Platform subscription',
      detail: unpaidPlatform
        ? 'Seats, integrations & storage · payment declined'
        : 'Seats, integrations & storage',
      amount: platformAmount,
      currency: 'USD',
      status: platformStatus,
      paidAt: paid ? issued.toISOString() : null,
      pdfUrl: paid ? `#platform-receipt-${yyyy}-${mm}` : null,
      paymentMethodLabel: paid
        ? 'Visa ···· 4242'
        : unpaidPlatform
          ? 'Visa ···· 4242'
          : null,
    };
    const receipts = [platformReceipt, ...demandReceipts];
    const amount = invoiceTotalAmount({ receipts, amount: platformAmount });
    invoices.push({
      id: `inv_demo_${yyyy}_${mm}`,
      kind: 'monthly',
      number: `INV-${yyyy}-${mm}`,
      label,
      detail: 'Subscription, seats, integrations & storage',
      amount,
      platformAmount,
      currency: 'USD',
      status: platformStatus,
      issuedAt: issued.toISOString(),
      paidAt: paid ? issued.toISOString() : null,
      pdfUrl: '#',
      paymentReceiptUrl: platformReceipt.pdfUrl,
      paymentMethodLabel: platformReceipt.paymentMethodLabel,
      receipts,
    });
  }

  return invoices;
}

/** Prepaid demand-fee invoices (invoice + payment receipt pair when paid). */
export function buildDemoDemandInvoices({ now = Date.now() } = {}) {
  const base = new Date(Number(now));
  const year = base.getFullYear();
  const rows = [
    {
      demandId: formatDemandId(year, 1071),
      debtor: 'Acme Freight',
      daysAgo: 0,
      status: 'OPEN',
      amount: 249,
    },
    {
      demandId: formatDemandId(year, 1063),
      debtor: 'Northwind Tools',
      daysAgo: 2,
      status: 'PAID',
      amount: 249,
    },
    {
      demandId: formatDemandId(year, 1055),
      debtor: 'Blue Harbor LLC',
      daysAgo: 7,
      status: 'PAID',
      amount: 249,
    },
    {
      demandId: formatDemandId(year, 1048),
      debtor: 'Summit Freight Co.',
      daysAgo: 11,
      status: 'PAID',
      amount: 249,
    },
  ];

  return rows.map((row, index) => {
    const issued = new Date(base);
    issued.setDate(issued.getDate() - row.daysAgo);
    issued.setHours(14, 30, 0, 0);
    const paid = row.status === 'PAID';
    return {
      id: `inv_demand_${row.demandId.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
      kind: 'demand',
      demandId: row.demandId,
      number: `INV-${row.demandId}`,
      label: `${row.demandId} · ${row.debtor}`,
      detail: 'Demand fee · paid upfront before letter sent',
      amount: row.amount,
      currency: 'USD',
      status: row.status,
      issuedAt: issued.toISOString(),
      paidAt: paid ? issued.toISOString() : null,
      pdfUrl: '#',
      paymentReceiptUrl: paid ? `#receipt-${index}` : null,
      paymentMethodLabel: paid ? 'Visa ···· 4242' : null,
      receipts: [],
    };
  });
}

/** Demo seed: 12 monthly invoices, each with 30 payment receipts. */
export function buildDemoBillingInvoices({
  now = Date.now(),
  months = PLATFORM_INVOICE_RETENTION_MONTHS,
  receiptsPerMonth = DEMO_RECEIPTS_PER_MONTH,
} = {}) {
  return retainPlatformInvoices(
    buildDemoMonthlyInvoices(months, { now, receiptsPerMonth }),
    { now }
  );
}

/** True when at least one monthly invoice has an UNPAID platform receipt. */
export function hasUnpaidPlatformDemoSeed(invoices = []) {
  return (Array.isArray(invoices) ? invoices : []).some((inv) => {
    if (invoiceKind(inv) !== 'monthly') return false;
    return invoiceReceipts(inv).some(
      (row) =>
        String(row?.kind || '').toLowerCase() === 'platform' &&
        String(row?.status || '').toUpperCase() === 'UNPAID'
    );
  });
}

/** True when demand receipt IDs use the canonical DJ-YYYY-###### format. */
export function hasCanonicalDemandIdSeed(invoices = []) {
  const demandReceipts = (Array.isArray(invoices) ? invoices : []).flatMap((inv) =>
    invoiceReceipts(inv).filter((row) => String(row?.kind || '').toLowerCase() === 'demand')
  );
  if (!demandReceipts.length) return false;
  return demandReceipts.every((row) => isValidDemandId(row.demandId));
}

/** True when demo seed already has 12 months × N nested receipts + unpaid cases. */
export function hasHeavyInvoiceReceiptSeed(
  invoices = [],
  {
    months = PLATFORM_INVOICE_RETENTION_MONTHS,
    receiptsPerMonth = DEMO_RECEIPTS_PER_MONTH,
  } = {}
) {
  const monthly = (Array.isArray(invoices) ? invoices : []).filter(
    (inv) => invoiceKind(inv) === 'monthly'
  );
  if (monthly.length < months) return false;
  if (!hasUnpaidPlatformDemoSeed(monthly)) return false;
  if (!hasCanonicalDemandIdSeed(monthly)) return false;
  return monthly.every((inv) => {
    const receipts = invoiceReceipts(inv);
    const hasPlatform = receipts.some((row) => row.kind === 'platform');
    return hasPlatform && receipts.length >= receiptsPerMonth + 1;
  });
}
